import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Ruler, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Realisations = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState({});

  // Résidence ELEVEN avec carrousel d'images
  const residenceEleven = {
    id: 'eleven',
    title: "Résidence ELEVEN",
    location: "Épalinges, Route de Berne 223",
    description: "Immeuble résidentiel et commercial de 9 logements avec installation photovoltaïque. Maîtrise complète du cycle immobilier.",
    images: [
      "https://customer-assets.emergentagent.com/job_982f1f8b-d55b-4c68-9981-e0def6f33939/artifacts/djjposso_Screenshot%202026-03-22%20at%2002.38.55.png",
      "https://customer-assets.emergentagent.com/job_property-promo-3/artifacts/io3x3d9y_ChatGPT%20Image%205%20nov.%202025%2C%2002_47_40.png",
      "https://customer-assets.emergentagent.com/job_property-promo-3/artifacts/urn1votf_ChatGPT%20Image%205%20nov.%202025%2C%2002_47_48.png",
      "https://customer-assets.emergentagent.com/job_property-promo-3/artifacts/djk5b6u3_ChatGPT%20Image%205%20nov.%202025%2C%2002_50_49.png",
      "https://customer-assets.emergentagent.com/job_property-promo-3/artifacts/jb1k07j4_ChatGPT%20Image%205%20nov.%202025%2C%2002_55_43.png"
    ],
    area: "600 m²",
    year: "2023-2024",
    type: "Immeuble PPE"
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
        
        // Initialiser l'index des images pour ELEVEN
        setCurrentImageIndex({ eleven: 0 });
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Erreur lors du chargement des projets');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => ({
      ...prev,
      eleven: prev.eleven === 0 ? residenceEleven.images.length - 1 : prev.eleven - 1
    }));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => ({
      ...prev,
      eleven: prev.eleven === residenceEleven.images.length - 1 ? 0 : prev.eleven + 1
    }));
  };

  if (loading) {
    return (
      <section id="realisations" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xl text-gray-600">Chargement...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="realisations" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1e5a7a] mb-4">
            Nos Réalisations
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Découvrez nos projets d'exception, témoins de notre expertise et de notre engagement envers l'excellence.
          </p>
        </div>

        {/* Résidence ELEVEN - Mise en avant */}
        <div className="mb-12">
          <Card
            onClick={() => navigate('/realisation/eleven')}
            className="group overflow-hidden border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 cursor-pointer bg-white"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Carrousel d'images */}
              <div className="relative h-96 lg:h-auto overflow-hidden">
                <img
                  src={residenceEleven.images[currentImageIndex.eleven || 0]}
                  alt={residenceEleven.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                
                {/* Contrôles du carrousel */}
                <button
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors z-10"
                >
                  <ChevronLeft size={24} className="text-[#1e5a7a]" />
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors z-10"
                >
                  <ChevronRight size={24} className="text-[#1e5a7a]" />
                </button>

                {/* Indicateurs */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                  {residenceEleven.images.map((_, index) => (
                    <div
                      key={index}
                      className={`h-2 rounded-full transition-all ${
                        index === (currentImageIndex.eleven || 0)
                          ? 'w-8 bg-[#f5b800]'
                          : 'w-2 bg-white/60'
                      }`}
                    />
                  ))}
                </div>

                <div className="absolute top-4 left-4">
                  <span className="bg-[#f5b800] text-black px-4 py-2 rounded-full text-sm font-bold">
                    ⭐ RÉALISATION PHARE
                  </span>
                </div>
              </div>

              {/* Contenu */}
              <CardContent className="p-8 lg:p-12 flex flex-col justify-center">
                <h3 className="text-3xl md:text-4xl font-bold text-[#1e5a7a] mb-4 group-hover:text-[#f5b800] transition-colors">
                  {residenceEleven.title}
                </h3>
                <div className="flex items-center text-gray-600 mb-4">
                  <MapPin size={20} className="mr-2" />
                  <span className="text-lg">{residenceEleven.location}</span>
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                  {residenceEleven.description}
                </p>
                <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-6">
                  <div className="flex items-center">
                    <Ruler size={18} className="mr-2 text-[#f5b800]" />
                    <span className="font-medium">{residenceEleven.area}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar size={18} className="mr-2 text-[#f5b800]" />
                    <span className="font-medium">{residenceEleven.year}</span>
                  </div>
                  <span className="bg-[#1e5a7a] text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {residenceEleven.type}
                  </span>
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/realisation/eleven');
                  }}
                  className="bg-[#1e5a7a] hover:bg-[#f5b800] hover:text-black text-white font-semibold transition-all duration-300 w-full lg:w-auto"
                >
                  Découvrir le projet complet
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </CardContent>
            </div>
          </Card>
        </div>

        {/* Section supprimée - Affichage uniquement de la Résidence ELEVEN */}
      </div>
    </section>
  );
};